import { useQuery, useQueryClient } from '@tanstack/react-query';
import chroma from 'chroma-js';
import React, { useEffect, useMemo, useState } from 'react';
import { ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Button, Chip, Icon, Input, Table, TableColumn, TableVariant, Toggle } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import {
  CopyInput,
  EntityCard,
  Loading,
  Notification,
  PageHeader,
  RenderFields,
  RestrictedBanner,
  SectionHeader,
} from '~/components';
import { BankAccountDropdown, useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { EntityDropdown } from '~/components/fields/EntityDropdown';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useCreateBankAccount, useUpdateBankAccount } from '~/lib/api/bankAccounts';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Flex, FormLabel, FormText, Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { AccountNumberModal } from './_components/AccountNumberModal';
import { BankAccountSectionProps } from './Section';

interface Params {
  id: string;
}

interface AccountNumberRow extends AccountNumber {
  isDefaultAccountNumber: boolean;
}

const accountNumberColumns: TableColumn<AccountNumberRow>[] = [
  {
    header: 'Description',
    accessor: 'description',
    width: { min: 'min-content', max: '2fr' },
    cell: ({ row, value }) => (
      <Table.TextCell value={value} weight="medium">
        {row.isDefaultAccountNumber && (
          <Flex gap={4}>
            <Chip>Default</Chip>
          </Flex>
        )}
      </Table.TextCell>
    ),
  },
  {
    header: 'Routing Number',
    accessor: 'routingNumber',
    width: { min: 'min-content', max: '1fr' },
    cell: Table.TextCell,
  },
  {
    header: 'Account Number',
    accessor: 'accountNumber',
    width: { min: 'min-content', max: '1fr' },
    cell: Table.TextCell,
  },
  {
    header: 'Account Number ID',
    accessor: 'id',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell displayVariant="monospaced" value={value} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { fixed: 100 },
    cell: Table.TimeCell,
  },
];

export const Balance = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-gap: 24px;
  grid-column: 1 / 3;

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  &[data-disabled='true'] {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      cursor: not-allowed;
      z-index: 11;
      transition: background-color 0.2s;
    }

    &:hover {
      &:before {
        background-color: ${({ theme }) => chroma(theme.background).alpha(0.4).hex('rgba')};
      }
    }
  }
`;

export const FormTextAmount = styled(FormText)<{ isNegative?: boolean; isAvailable?: boolean }>`
  font-size: 20px;
  font-weight: 600;
  padding: 0;
  color: ${({ theme }) => theme.foreground};

  ${({ isNegative }) =>
    isNegative &&
    css`
      color: ${({ theme }) => theme.danger.background};
    `}

  ${({ isAvailable, isNegative }) =>
    isAvailable &&
    !isNegative &&
    css`
      color: ${({ theme }) => theme.primary.background};
    `}
`;

export const FormLabelAmount = styled(FormLabel)`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

interface BankAccountForm {
  id?: string;
  createdAt?: Date;
  description: string;
  displayName?: string;
  entityId: string;
  isOverdraftable?: boolean;
  overdraftReserveAccountId?: string;
  type?: string;
}

export const PageBankAccountsEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const queryClient = useQueryClient();
  const { cursor, onNext, setPageSize, ...pagination } = usePaginationState();
  const [modalState, setModalState] = useState<{ isOpen: boolean; accountNumber?: AccountNumber }>({ isOpen: false });

  const outletContext = useOutletContext<BankAccountSectionProps>();
  const bankAccount = outletContext?.bankAccount;

  const createBankAccount = useCreateBankAccount();
  const updateBankAccount = useUpdateBankAccount();

  const { options: bankAccountList } = useBankAccountOptions({
    filters: { type: 'OVERDRAFT_RESERVE' },
  });

  const formMethods = useForm<BankAccountForm>({
    defaultValues: {
      id: bankAccount?.id,
      createdAt:
        bankAccount?.createdAt &&
        typeof bankAccount.createdAt === 'object' &&
        !isNaN((bankAccount.createdAt as Date).getTime())
          ? (bankAccount.createdAt as Date)
          : undefined,
      description: bankAccount?.description ?? '',
      displayName: bankAccount?.displayName ?? '',
      entityId: bankAccount?.owners?.[0] ?? '',
      isOverdraftable: bankAccount?.isOverdraftable ?? false,
      overdraftReserveAccountId: bankAccount?.overdraftReserveAccountId ?? '',
      type: bankAccount?.type ?? '',
    },
  });
  const { handleSubmit, setValue, watch } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();

  useEffect(() => {
    if (!bankAccount) return;
    setValue('id', bankAccount.id);
    setValue(
      'createdAt',
      bankAccount.createdAt &&
        typeof bankAccount.createdAt === 'object' &&
        !isNaN((bankAccount.createdAt as Date).getTime())
        ? (bankAccount.createdAt as Date)
        : undefined
    );
    setValue('description', bankAccount.description ?? '');
    setValue('displayName', bankAccount.displayName ?? '');
    setValue('entityId', bankAccount.owners?.[0] ?? '');
    setValue('isOverdraftable', bankAccount.isOverdraftable ?? false);
    setValue('overdraftReserveAccountId', bankAccount.overdraftReserveAccountId ?? '');
    setValue('type', bankAccount.type ?? '');
  }, [bankAccount, setValue]);

  const onSubmit = (data: BankAccountForm) => {
    if (!currentUser) return;
    if (id) {
      updateBankAccount.mutate(
        {
          path: { bank_account_id: id },
          body: {
            description: data.description,
            display_name: data.displayName,
            is_overdraftable: data.isOverdraftable,
            overdraft_reserve_account_id: data.isOverdraftable ? data.overdraftReserveAccountId : undefined,
          },
        },
        {
          onSuccess: (response) => {
            addSuccessNotification({
              content:
                response.display_name || response.description
                  ? `${response.display_name || response.description} updated`
                  : 'Updated',
            });
          },
          onError: (error) => {
            addDangerNotification({ content: error.message ?? 'Failed to update bank account' });
          },
        }
      );
      return;
    }

    createBankAccount.mutate(
      {
        body: {
          entity_id: data.entityId,
          description: data.description,
          display_name: data.displayName,
          is_overdraftable: data.isOverdraftable,
          overdraft_reserve_account_id: data.isOverdraftable ? data.overdraftReserveAccountId : undefined,
        },
      },
      {
        onSuccess: (response) => {
          navigate(ROUTE.BANK_ACCOUNTS);
          addSuccessNotification({
            content:
              response.display_name || response.description
                ? `${response.display_name || response.description} created`
                : 'Created',
          });
        },
        onError: (error) => {
          addDangerNotification({ content: error.message ?? 'Failed to create bank account' });
        },
      }
    );
  };

  const hasUpdatePermissions = isAuthorized({
    permission: 'bankAccounts',
    permissionLevel: 'update',
    bankAccount: { id, owners: bankAccount?.owners },
  });

  const { data: accountNumbersData, isLoading: isAccountNumbersLoading } = useQuery({
    queryKey: ['bank-account-account-numbers', id, cursor, pagination.pageSize],
    queryFn: async () => {
      if (!id) {
        return { accountNumbers: [], hasMore: false };
      }
      return BankAccountRepository.getAllAccountNumber(id, {
        limit: pagination.pageSize,
        startingAfter: cursor,
      });
    },
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
  const accountNumberRows = useMemo(() => {
    return (accountNumbersData?.accountNumbers ?? []).map((accountNumber) => ({
      ...accountNumber,
      isDefaultAccountNumber: accountNumber.id === bankAccount?.defaultAccountNumberId,
    }));
  }, [accountNumbersData, bankAccount]);
  const showRoutingNumber = useMemo(() => {
    if (accountNumberRows.length === 0) return false;
    return !accountNumberRows.every((accountNumber) => accountNumber.routingNumber);
  }, [accountNumberRows]);

  return (
    <>
      <AccountNumberModal
        accountNumber={modalState.accountNumber}
        bankAccountId={id}
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ isOpen: false });
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['bank-account-account-numbers', id] });
        }}
      />
      {!id && (
        <PageHeader text={id ? bankAccount?.displayName || bankAccount?.description || 'Account' : 'Create Account'}>
          <Button onClick={() => navigate(-1)} variant="secondary" size="small">
            {id ? 'Back' : 'Cancel'}
          </Button>
          <Button type="submit" isDisabled={!hasUpdatePermissions} size="small" icon={!id && <Icon.Plus />}>
            {id ? 'Save' : 'Create Account'}
          </Button>
        </PageHeader>
      )}

      <Loading isLoading={false} fullHeight>
        {!hasUpdatePermissions && (
          <Inner py={0}>
            <RestrictedBanner />
          </Inner>
        )}

        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
            {RenderFields<BankAccountForm>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'displayName',
                      label: 'Display Name',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Display Name" />
                      ),
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      rules: { required: true },
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Description" />
                      ),
                    },
                    {
                      id: 'id',
                      label: 'ID',
                      hide: !id,
                      children: ({ value }) => <CopyInput value={String(value)} />,
                    },
                    {
                      id: 'createdAt',
                      label: 'Created',
                      hide: !id,
                      children: ({ value }) => (
                        <CopyInput value={value ? formatDateTime(value as Date, { timeZoneFormat: 'utc' }) : '-'} />
                      ),
                    },

                    {
                      id: 'entityId',
                      label: 'Entity',
                      rules: { required: true },
                      children: (field: ControllerRenderProps<BankAccountForm, any>) =>
                        id ? (
                          <EntityCard entityId={field.value} />
                        ) : (
                          <>
                            <Flex gap={8}>
                              <EntityDropdown
                                fullWidth
                                value={field.value}
                                onChange={field.onChange}
                                searchLabel="Business or person name..."
                                placeholder="Select Entity"
                              />
                              {!watch('entityId') && (
                                <Button
                                  variant="secondary"
                                  onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)}
                                  size="small"
                                  icon={<Icon.Plus />}
                                >
                                  Add Entity
                                </Button>
                              )}
                            </Flex>
                          </>
                        ),
                    },
                  ],
                },
                {
                  headline: 'Overdraft',
                  fields: [
                    {
                      hide: bankAccountList?.length > 0,
                      content: (
                        <Notification
                          color="danger"
                          withClose={false}
                          variant="light"
                          actionButton={{
                            label: 'Create',
                            onClick: () => {
                              if (currentPlatform?.isLiveEnabled) {
                                navigate(ROUTE.PLATFORM);
                                return;
                              }
                              navigate(ROUTE.PLATFORM_ROOT_ENTITY);
                            },
                          }}
                        >
                          You will need to create a root entity first to select the overdraft reserve account.
                        </Notification>
                      ),
                    },
                    {
                      hide: bankAccount?.type !== 'OVERDRAFT_RESERVE',
                      content: (
                        <Notification color="danger" withClose={false} variant="light">
                          Overdraft reserve accounts cannot be overdrafted.
                        </Notification>
                      ),
                    },
                    {
                      id: 'isOverdraftable',
                      label: 'Overdraftable',
                      children: ({ value, onChange }) => (
                        <FormText>
                          <Toggle
                            isChecked={!!value}
                            onCheckedChange={onChange}
                            isDisabled={
                              (bankAccountList?.length && bankAccountList.length < 1) || !bankAccountList?.length
                            }
                          />
                        </FormText>
                      ),
                    },
                    {
                      id: 'overdraftReserveAccountId',
                      label: 'Reserve Account ID',
                      children: ({ value, onChange }) => (
                        <BankAccountDropdown
                          value={String(value)}
                          onChange={onChange}
                          filters={{ type: 'OVERDRAFT_RESERVE' }}
                          excludeBankAccountIds={bankAccount?.id ? [bankAccount.id] : undefined}
                          fullWidth
                          maxWidth="640px"
                          isDisabled={!watch('isOverdraftable')}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
            <Inner px="var(--page-margin)">
              <EditToolbar>
                <Button onClick={() => navigate(-1)} variant="secondary" size="small" type="button">
                  {id ? 'Back' : 'Cancel'}
                </Button>
                <Button type="submit" isDisabled={!hasUpdatePermissions} size="small" icon={!id && <Icon.Plus />}>
                  {id ? 'Save' : 'Create Account'}
                </Button>
              </EditToolbar>
            </Inner>
          </form>

          {id && (
            <>
              <SectionHeader text="Account Numbers" border>
                <Button
                  icon={<Icon.Plus />}
                  isDisabled={!hasUpdatePermissions}
                  onClick={() => {
                    setModalState({ isOpen: true });
                  }}
                  size="small"
                >
                  Create
                </Button>
              </SectionHeader>

              <Inner pt={0} px="var(--page-margin)">
                <Table<AccountNumberRow>
                  columns={accountNumberColumns}
                  columnVisibility={{
                    accountNumber: true,
                    bankAccountId: true,
                    bic: false,
                    checkIssuingPositivePayRequired: false,
                    createdAt: true,
                    description: true,
                    id: true,
                    isDefaultAccountNumber: true,
                    routingNumber: showRoutingNumber,
                  }}
                  data={accountNumberRows}
                  pagination={{
                    hasNext: accountNumbersData?.hasMore ?? false,
                    onNext: () =>
                      accountNumbersData?.accountNumbers.length
                        ? onNext({
                            cursor: accountNumbersData.accountNumbers[accountNumbersData.accountNumbers.length - 1].id,
                          })
                        : undefined,
                    onPageSizeChange: setPageSize,
                    ...pagination,
                  }}
                  isLoading={isAccountNumbersLoading}
                  variant={TableVariant.List}
                  rowAction={(row) => (
                    <Button
                      size="tiny"
                      variant="secondary"
                      icon={<Icon.Edit />}
                      onClick={() => {
                        setModalState({ isOpen: true, accountNumber: row });
                      }}
                    >
                      Edit
                    </Button>
                  )}
                />
              </Inner>
            </>
          )}
        </FormProvider>
      </Loading>
    </>
  );
};
